import { useEffect, useState } from "react";
import { api, formatCurrency, monthNames, getErrorMessage } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Target, Loader2, BellRing, Send } from "lucide-react";
import { toast } from "sonner";

/**
 * Per-seller monthly objective progress gauges on the dashboard.
 * Shows one row per seller with a colored progress bar (CA vs monthly target).
 */
export const SellerMonthlyProgressCard = ({ year, month }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alertSettings, setAlertSettings] = useState({ enabled: false, threshold_percent: 70, alert_on_exceed: true });
  const [savingAlerts, setSavingAlerts] = useState(false);
  const [sendingAlerts, setSendingAlerts] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [res, settings] = await Promise.all([
          api.getSellerMonthlyProgress({ year, month }),
          api.getSellerAlertSettings().catch(() => ({ data: null })),
        ]);
        setData(res.data);
        if (settings.data) setAlertSettings(settings.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [year, month]);

  const saveAlertSettings = async (next) => {
    try {
      setSavingAlerts(true);
      await api.updateSellerAlertSettings(next);
      setAlertSettings(next);
      toast.success("Paramètres d'alertes enregistrés");
    } catch (e) {
      toast.error(getErrorMessage(e, "Erreur"));
    } finally {
      setSavingAlerts(false);
    }
  };

  const sendAlertsNow = async () => {
    try {
      setSendingAlerts(true);
      const res = await api.runSellerAlertsNow();
      toast.success(`${res.data.emails_sent} email(s) envoyé(s) (${res.data.period})`);
    } catch (e) {
      toast.error(getErrorMessage(e, "Les alertes doivent d'abord être activées"));
    } finally {
      setSendingAlerts(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  const sellers = data?.sellers || [];
  if (sellers.length === 0) return null;

  return (
    <Card data-testid="seller-monthly-progress">
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2">
          <Target className="h-5 w-5 text-primary" />
          Progression objectif — {monthNames[(data?.month || month || new Date().getMonth() + 1) - 1]} {data?.year || year}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="p-3 rounded-sm border bg-muted/30 space-y-3" data-testid="alert-settings-block">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <p className="text-sm font-medium flex items-center gap-2">
                <BellRing className="h-4 w-4 text-primary" />
                Alertes email automatiques (1er du mois)
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Envoie au vendeur un email sur son objectif du mois précédent (alerte si non atteint, félicitations si dépassé).
              </p>
            </div>
            <Switch
              checked={alertSettings.enabled}
              onCheckedChange={(v) => saveAlertSettings({ ...alertSettings, enabled: v })}
              disabled={savingAlerts}
              data-testid="alert-toggle"
            />
          </div>
          {alertSettings.enabled && (
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <Label className="text-xs">Seuil alerte (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  className="w-20 h-8"
                  value={alertSettings.threshold_percent}
                  onChange={(e) => setAlertSettings({ ...alertSettings, threshold_percent: Number(e.target.value) })}
                  onBlur={() => saveAlertSettings(alertSettings)}
                  data-testid="alert-threshold"
                />
              </div>
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={alertSettings.alert_on_exceed}
                  onChange={(e) => saveAlertSettings({ ...alertSettings, alert_on_exceed: e.target.checked })}
                  data-testid="alert-exceed-toggle"
                />
                Félicitations en cas de dépassement
              </label>
              <Button
                variant="outline"
                size="sm"
                onClick={sendAlertsNow}
                disabled={sendingAlerts}
                data-testid="send-alerts-now"
                className="ml-auto"
              >
                {sendingAlerts ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Send className="h-3 w-3 mr-1" />}
                Envoyer maintenant (test)
              </Button>
            </div>
          )}
        </div>

        {sellers.map((s) => {
          const target = s.monthly_target || 0;
          const current = s.current_ca || 0;
          const pct = target > 0 ? Math.min(100, Math.round((current / target) * 100)) : 0;
          const overshoot = target > 0 && current > target ? Math.round((current / target) * 100) : null;
          const color = !target ? "bg-slate-300"
            : current >= target ? "bg-green-500"
            : pct >= 70 ? "bg-amber-500"
            : "bg-primary";
          return (
            <div key={s.seller_id} className="space-y-1" data-testid={`progress-${s.seller_id}`}>
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium">{s.seller_name}</span>
                <span className="tabular-nums text-xs">
                  <span className="currency font-semibold">{formatCurrency(current)}</span>
                  {target > 0 ? (
                    <>
                      {" / "}
                      <span className="text-muted-foreground">{formatCurrency(target)}</span>
                      <span className={`ml-2 font-semibold ${current >= target ? "text-green-600" : "text-muted-foreground"}`}>
                        {overshoot !== null ? `${overshoot}%` : `${pct}%`}
                      </span>
                      {!s.has_explicit_monthly && (
                        <span className="ml-2 text-[10px] text-muted-foreground italic">(annuel/12)</span>
                      )}
                    </>
                  ) : (
                    <span className="ml-2 text-xs text-muted-foreground italic">Aucun objectif</span>
                  )}
                </span>
              </div>
              <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                <div className={`h-full ${color} transition-all`} style={{ width: `${target > 0 ? pct : 0}%` }} />
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default SellerMonthlyProgressCard;
